import type {ComponentProps, FC, ReactNode} from 'react';
import {createContext, useContext, useMemo} from 'react';
import {Outlet, matchPath, useLocation} from 'react-router-dom';
import {PageHeader} from 'components/PageHeader';
import {TabBar as TabBarComponent} from './TabBar';
import type {TabbedPageTab, TabbedPageTabs} from './types';

type TabbedPageContextValue = {
  tabs: TabbedPageTabs;
  activeTab: TabbedPageTab;
};

const TabbedPageContext = createContext<TabbedPageContextValue | null>(null);

const useTabbedPage = (): TabbedPageContextValue => {
  const context = useContext(TabbedPageContext);
  if (!context) {
    throw new Error('TabbedPage parts must be rendered inside <TabbedPage>');
  }
  return context;
};

type RootProps = {
  tabs: TabbedPageTabs;
  children: ReactNode;
};

const Root: FC<RootProps> = ({tabs, children}) => {
  const {pathname} = useLocation();

  const activeTab =
    tabs.find(({path}) => matchPath(path, pathname) !== null) ?? tabs[0];

  const value = useMemo(() => ({tabs, activeTab}), [tabs, activeTab]);

  return (
    <TabbedPageContext.Provider value={value}>
      {children}
    </TabbedPageContext.Provider>
  );
};

type HeaderProps = Omit<ComponentProps<typeof PageHeader>, 'ActionButton'>;

const Header: FC<HeaderProps> = ({title, description, ...props}) => {
  const {activeTab} = useTabbedPage();

  return (
    <PageHeader
      {...props}
      title={activeTab.title ?? title}
      description={activeTab.description ?? description}
      ActionButton={activeTab.ActionButton}
    />
  );
};

type TabBarProps = {
  ariaLabel: string;
};

const TabBar: FC<TabBarProps> = ({ariaLabel}) => {
  const {tabs, activeTab} = useTabbedPage();

  return (
    <TabBarComponent tabs={tabs} activeTab={activeTab} ariaLabel={ariaLabel} />
  );
};

const Content: FC = () => {
  useTabbedPage();
  return <Outlet />;
};

export const TabbedPage = Object.assign(Root, {Header, TabBar, Content});
